import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';

export const ANNOTATION_SEPARATOR = '|||ANN|||';

export interface TaskAnnotationItem {
  text: string;
  authorString: string;
  time: string;
}

/**
 * Build annotations array from task or API response. Annotations live only in comments (-> Task.note);
 * task.description is the task's own descriptor and is never read back as an annotation.
 * Comments are either a JSON array or legacy plain text split by ANNOTATION_SEPARATOR.
 */
export function taskToAnnotations(
  task: WrappedTask | Record<string, unknown> | null | undefined,
  fallbackAuthor?: string
): TaskAnnotationItem[] {
  if (!task) return [];

  const t = task as any;
  const commentsStr = t?.getComments?.() ?? t?.comments ?? '';

  try {
    if (commentsStr.trim()) {
      const parsed = JSON.parse(commentsStr);
      if (Array.isArray(parsed)) {
        const taskTime =
          t?.lastModified != null
            ? String(t.lastModified)
            : t?.authoredOn != null
              ? String(t.authoredOn)
              : '';
        const items: TaskAnnotationItem[] = parsed.map((entry: any) => {
          const entryTime = typeof entry?.time === 'string' && entry.time.trim() ? entry.time : '';
          const time = entryTime || taskTime;
          return {
            text: String(entry?.text ?? ''),
            authorString:
              String(entry?.authorString ?? '').trim() || (fallbackAuthor?.trim() ?? ''),
            time,
          };
        });
        return items;
      }
    }
  } catch {
    // not JSON — use legacy format
  }

  const resolved: string =
    t?.getOwnerDisplay?.() ??
    t?.getOwnerId?.() ??
    t?.owner?.display ??
    (typeof t?.owner?.reference === 'string' ? t.owner.reference : '') ??
    '';
  const authorString: string = resolved.trim() || (fallbackAuthor?.trim() ?? '');
  const legacyTime: string =
    t?.lastModified != null
      ? String(t.lastModified)
      : t?.authoredOn != null
        ? String(t.authoredOn)
        : '';

  const items: TaskAnnotationItem[] = [];

  const commentParts = commentsStr
    .split(ANNOTATION_SEPARATOR)
    .map((s: string) => s.trim())
    .filter(Boolean);

  commentParts.forEach((text: string) => {
    items.push({ text, authorString, time: legacyTime });
  });

  return items;
}

export interface AnnotationsPayload {
  comments: string;
}

/** Convert annotations to backend payload. Never returns a description — that belongs to the task. */
export function annotationsToPayload(
  annotations: TaskAnnotationItem[]
): AnnotationsPayload | null {
  if (!annotations.length) return null;
  const firstText = annotations[0]?.text?.trim();
  if (!firstText) return null;

  const comments = JSON.stringify(
    annotations.map((a) => ({
      text: a.text,
      authorString: a.authorString,
      time: a.time,
    }))
  );

  return { comments };
}

/** Annotation text is stored as `description\n\ncomment`; returns just the description part. */
export function getAnnotationDescriptionPart(text: string | undefined | null): string {
  if (!text) return '';
  const idx = text.indexOf('\n\n');
  return (idx === -1 ? text : text.slice(0, idx)).trim();
}

export const ANNOTATION_PREVIEW_LIMIT = 5;
export const ANNOTATION_TRUNCATE_LENGTH = 40;
